import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import UserActivityReportPanel from "../components/UserActivityReportPanel";
import { useAppDispatch, useAppSelector } from "../store";
import {
  addSubscription,
  cancelSubscription,
  fetchUserDetails,
  fetchUserSubscriptions,
  updateUser,
} from "../store/slices/adminUsersSlice";

import type { SelectChangeEvent } from "@mui/material/Select";
import type { ChangeEvent } from "react";

interface EditFormState {
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  status: string;
}
interface SubscriptionFormState {
  plan: string;
  duration: number; // days
}
const UserDetailPage = (): React.ReactElement => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { selectedUser, userStats, loading, error } = useAppSelector(
    (state) => state.adminUsers,
  );
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState<EditFormState>({
    firstName: "",
    lastName: "",
    email: "",
    role: "",
    status: "",
  });
  const [subscriptionForm, setSubscriptionForm] =
    useState<SubscriptionFormState>({
      plan: "PREMIUM",
      duration: 30,
    });
  const [subscriptionError, setSubscriptionError] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (userId) {
      dispatch(fetchUserDetails(userId));
      dispatch(fetchUserSubscriptions(userId));
    }
  }, [dispatch, userId]);

  useEffect(() => {
    if (selectedUser) {
      setEditForm({
        firstName: selectedUser.firstName || "",
        lastName: selectedUser.lastName || "",
        email: selectedUser.email,
        role: selectedUser.role,
        status: selectedUser.status,
      });
    }
  }, [selectedUser]);

  const handleSaveUser = async (): Promise<void> => {
    if (userId) {
      await dispatch(updateUser({ userId, data: editForm }));
      setEditDialogOpen(false);
      dispatch(fetchUserDetails(userId));
    }
  };

  const handleAddSubscription = async (): Promise<void> => {
    if (userId) {
      setSubscriptionError(null);
      const result = await dispatch(
        addSubscription({
          userId,
          plan: subscriptionForm.plan,
          duration: subscriptionForm.duration,
        }),
      );
      if (addSubscription.fulfilled.match(result)) {
        setSubscriptionDialogOpen(false);
        dispatch(fetchUserDetails(userId));
        dispatch(fetchUserSubscriptions(userId));
      } else if (addSubscription.rejected.match(result)) {
        setSubscriptionError(result.payload || "Failed to add subscription");
      }
    }
  };

  const handleCancelSubscription = async (
    subscriptionId: string,
  ): Promise<void> => {
    if (window.confirm("Are you sure you want to cancel this subscription?")) {
      await dispatch(cancelSubscription(subscriptionId));
      if (userId) {
        dispatch(fetchUserDetails(userId));
        dispatch(fetchUserSubscriptions(userId));
      }
    }
  };

  const getStatusColor = (
    status: string,
  ): "default" | "success" | "error" | "warning" | "info" => {
    switch (status) {
      case "ACTIVE":
        return "success";
      case "EXPIRED":
        return "error";
      case "CANCELLED":
        return "warning";
      case "PENDING":
        return "info";
      default:
        return "default";
    }
  };

  const getRoleColor = (role: string): "default" | "primary" => {
    switch (role) {
      case "ADMIN":
        return "primary";
      case "USER":
        return "default";
      default:
        return "default";
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!selectedUser) {
    return (
      <Box>
        <Alert severity="error">User not found</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/users")}
        >
          Back to Users
        </Button>
        <Typography variant="h4">User Details: {selectedUser.email}</Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6">Basic Information</Typography>
                <Button
                  startIcon={<EditIcon />}
                  onClick={() => setEditDialogOpen(true)}
                >
                  Edit
                </Button>
              </Box>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="body2" color="textSecondary">
                    First Name
                  </Typography>
                  <Typography variant="body1">
                    {selectedUser.firstName || "-"}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="body2" color="textSecondary">
                    Last Name
                  </Typography>
                  <Typography variant="body1">
                    {selectedUser.lastName || "-"}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="body2" color="textSecondary">
                    Email
                  </Typography>
                  <Typography variant="body1">{selectedUser.email}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="body2" color="textSecondary">
                    Role
                  </Typography>
                  <Chip
                    label={selectedUser.role}
                    size="small"
                    color={getRoleColor(selectedUser.role)}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="body2" color="textSecondary">
                    Status
                  </Typography>
                  <Chip
                    label={selectedUser.status || "ACTIVE"}
                    size="small"
                    color={getStatusColor(selectedUser.status || "ACTIVE")}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="body2" color="textSecondary">
                    Created
                  </Typography>
                  <Typography variant="body1">
                    {new Date(selectedUser.createdAt).toLocaleDateString()}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="body2" color="textSecondary">
                    Last Login
                  </Typography>
                  <Typography variant="body1">
                    {selectedUser.lastLoginAt
                      ? new Date(selectedUser.lastLoginAt).toLocaleString()
                      : "Not recorded yet"}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Statistics */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Statistics
              </Typography>
              {userStats ? (
                <Grid container spacing={2}>
                  {[
                    ["Texts practiced", userStats.practicedTexts],
                    ["Listening", `${userStats.listeningMinutes} min`],
                    ["Completed dictations", userStats.completedDictations],
                    ["Dictation time", `${userStats.dictationMinutes} min`],
                    ["Saved words", userStats.savedWords],
                    ["Word reviews", userStats.wordReviews],
                  ].map(
                    ([label, value]): React.ReactElement => (
                      <Grid size={{ xs: 6 }} key={label}>
                        <Typography variant="body2" color="textSecondary">
                          {label}
                        </Typography>
                        <Typography variant="h6">{value}</Typography>
                      </Grid>
                    ),
                  )}
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="body2" color="textSecondary">
                      Last practice
                    </Typography>
                    <Typography variant="body1">
                      {userStats.lastPracticeAt
                        ? new Date(userStats.lastPracticeAt).toLocaleString()
                        : "No recorded practice"}
                    </Typography>
                  </Grid>
                </Grid>
              ) : (
                <Typography>No statistics available</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <UserActivityReportPanel userId={selectedUser.id} />
        </Grid>

        {/* Subscriptions */}
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6">Subscriptions</Typography>
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => setSubscriptionDialogOpen(true)}
                >
                  Add Subscription
                </Button>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Plan</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Start Date</TableCell>
                      <TableCell>End Date</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedUser.subscriptions?.map((subscription) => (
                      <TableRow key={subscription.id}>
                        <TableCell>
                          {typeof subscription.plan === "object" &&
                          subscription.plan !== null
                            ? (
                                subscription.plan as {
                                  id: string;
                                  name: string;
                                }
                              ).name
                            : subscription.plan || "Unknown"}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={subscription.status}
                            size="small"
                            color={getStatusColor(subscription.status)}
                          />
                        </TableCell>
                        <TableCell>
                          {new Date(
                            subscription.startDate,
                          ).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {subscription.endDate
                            ? new Date(
                                subscription.endDate,
                              ).toLocaleDateString()
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {subscription.status === "ACTIVE" && (
                            <Button
                              size="small"
                              startIcon={<CancelIcon />}
                              onClick={() =>
                                handleCancelSubscription(subscription.id)
                              }
                            >
                              Cancel
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    )) || (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          No subscriptions found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Edit User Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 6 }}>
              <TextField
                label="First Name"
                value={editForm.firstName}
                onChange={(e) =>
                  setEditForm({ ...editForm, firstName: e.target.value })
                }
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                label="Last Name"
                value={editForm.lastName}
                onChange={(e) =>
                  setEditForm({ ...editForm, lastName: e.target.value })
                }
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Email"
                value={editForm.email}
                onChange={(e) =>
                  setEditForm({ ...editForm, email: e.target.value })
                }
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={editForm.role}
                  onChange={(e: SelectChangeEvent) =>
                    setEditForm({ ...editForm, role: e.target.value })
                  }
                >
                  <MenuItem value="USER">User</MenuItem>
                  <MenuItem value="ADMIN">Admin</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={editForm.status}
                  onChange={(e: SelectChangeEvent) =>
                    setEditForm({ ...editForm, status: e.target.value })
                  }
                >
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="INACTIVE">Inactive</MenuItem>
                  <MenuItem value="BANNED">Banned</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveUser} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Subscription Dialog */}
      <Dialog
        open={subscriptionDialogOpen}
        onClose={() => setSubscriptionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Subscription</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth>
                <InputLabel>Plan</InputLabel>
                <Select
                  value={subscriptionForm.plan}
                  onChange={(e: SelectChangeEvent) =>
                    setSubscriptionForm({
                      ...subscriptionForm,
                      plan: e.target.value,
                    })
                  }
                >
                  <MenuItem value="PREMIUM">Premium</MenuItem>
                  <MenuItem value="PRO">Pro</MenuItem>
                  <MenuItem value="BASIC">Basic</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Duration (days)"
                type="number"
                value={subscriptionForm.duration}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setSubscriptionForm({
                    ...subscriptionForm,
                    duration: parseInt(e.target.value, 10) || 0,
                  })
                }
                fullWidth
              />
            </Grid>
            {subscriptionError && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="error">{subscriptionError}</Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubscriptionDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleAddSubscription} variant="contained">
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserDetailPage;
